import React, { useEffect, useState, useCallback } from 'react';
import {
  Button,
  RadioButtonGroup,
  RadioButton,
  MultiSelect,
  DatePicker,
  DatePickerInput
} from 'carbon-components-react';
import { AutoRenewComponent, FormError } from 'components';
import { connect, useSelector, useDispatch } from 'react-redux';
import { RootState } from 'redux/root.reducer';
import { CFCProps } from 'types';
import { Undo16, ArrowRight16, Information16 } from '@carbon/icons-react';
import Utils from 'utilities/helper.util';
import { selectLodgeEnquiry } from 'redux/enquiries';
import { addAlert, removeAlertByGroup } from 'redux/alerts';
import dayjs from 'dayjs';
import { RETIRED_ACTIVITY_TYPES } from 'appConstants/enquiry';

const sameDayWarningGroup = 'same-day-warning';

const mapStateToProps = ({ lodgeEnquiryCache }: RootState) => ({
  lodgeEnquiryCache
});

const connector = connect(mapStateToProps);

type Props = CFCProps<typeof connector>;
const JobDateSelectionForm: React.FC<Props | any> = (props: any) => {
  const NEXT_SIX_MOTNHS: any = Utils.getDateAfterMonths(6);
  const NEXT_28_DAYS_DATE: any = Utils.getDateAfterDays(28);
  const {
    values,
    touched: fTouched,
    errors: fErrors,
    handleSubmit,
    updateValues,
    goToPreviousFormHandler
  } = props;
  const inputError = ((errors: any, touched: any) => (
    key:
      | 'digEndAt'
      | 'digStartAt'
      | 'autoRenewUntil'
      | 'enquiryType'
      | 'activityTypes'
  ) => errors[key] && touched[key] && <FormError>{errors[key]}</FormError>)(
    fErrors,
    fTouched
  );
  const { lodgeEnquiryCache } = props;
  const lodgeEnquiry: any = useSelector(selectLodgeEnquiry);
  const dispatch = useDispatch();

  const [lodgeEnquiryType, setLodgeEnquiryType] = useState<
    string | undefined
  >();
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(true);
  const [exActivityTypeItems, setExActivityTypeItems] = useState<any>([]);
  const [designActivityTypeItems, setDesignActivityTypeItems] = useState<any>(
    []
  );

  //TODO
  const [showAutoRenewToggle, setShowAutoRenewToggle] = useState<boolean>(
    false
  );
  const [autoRenewUntil, setAutoRenewUntil] = useState<any>(
    values.autoRenewUntil
  );
  const [isSameDayEnquiry, setIsSameDayEnquiry] = useState<boolean>(false);
  const [isMutliSelectExpanded, setIsMutliSelectExpanded] = useState<boolean>(
    false
  );
  const [autoRenewToggle, setAutoRenewToggle] = useState<boolean>(
    values.autoRenewToggle || false
  );
  // SET DATES
  const [digStartAtMin, setDigStartAtMin] = useState<any>(values.digStartAt);

  // USECALLBACK
  const getEnquiryTypeActivities = useCallback((activityTypeObj: Object) => {
    const { items, typeObj }: any = activityTypeObj;
    if (typeObj.length > 0 && typeof items === 'string' && items.length > 0) {
      const itemArr = items.split(',');
      const selectedItems = itemArr.map((item: any) => {
        return typeObj.find((i: any) => i.id === item);
      });

      return selectedItems.filter(Boolean);
    }
    return [];
  }, []);

  const hasRetiredExcavationActivity =
    values.isPlanningJob === false &&
    typeof values.activityTypes === 'string' &&
    values.activityTypes
      .split(',')
      .some((activityType: string) =>
        RETIRED_ACTIVITY_TYPES.includes(activityType)
      );

  const defaultActivities =
    values.isPlanningJob === null || hasRetiredExcavationActivity
      ? []
      : values.isPlanningJob === true
      ? getEnquiryTypeActivities({
          items: values.activityTypes,
          typeObj: designActivityTypeItems
        })
      : getEnquiryTypeActivities({
          items: values.activityTypes,
          typeObj: exActivityTypeItems
        });
  const validateFields = useCallback(() => {
    const { digEndAt, digStartAt, activityTypes, isPlanningJob } = values;

    const isActivityTypes =
      typeof activityTypes === 'object'
        ? activityTypes.length > 0
        : activityTypes !== '';
    const isValid =
      isPlanningJob !== null &&
      isActivityTypes &&
      digEndAt !== '' &&
      digEndAt !== undefined &&
      digStartAt !== '' &&
      digEndAt !== undefined;
    setIsButtonDisabled(!isValid);
    updateValues(values);
  }, [values, updateValues]);

  const enquiryTypeChangeHandler = (val: any) => {
    values.isPlanningJob = val === 'Design' ? true : false;
    values.enquiryType = val;
    values.activityTypes = [];
    setLodgeEnquiryType(val);
    setIsMutliSelectExpanded(true);
    validateFields();
  };

  const setStartDateInfo = (selectedDate: any) => {
    const isTodayDate = Utils.isSelectedTodayDate(selectedDate);
    setIsSameDayEnquiry(isTodayDate);
  };

  const setAutoRenewDate = (selectedDate: any) => {
    const isValidDate = Utils.isDateAfterExtMonth(selectedDate, 6);
    let _date = isValidDate ? NEXT_SIX_MOTNHS : selectedDate;
    _date = _date === 'Invalid Date' ? '' : _date;
    values.autoRenewUntil = _date;
    setAutoRenewUntil(_date);
  };

  const fieldsChangeHandler = (obj: any) => {
    const { key, data }: any = obj;
    if (key === 'digStartAt') {
      values.digStartAt = data;
      setDigStartAtMin(data);
      setStartDateInfo(data);
    }

    if (key === 'digEndAt') {
      values.digEndAt = data;
      const isAutoRenew: boolean = dayjs(data).isAfter(
        NEXT_28_DAYS_DATE,
        'date'
      );
      setAutoRenewDate(data);
      setShowAutoRenewToggle(isAutoRenew);
    }

    if (key === 'activityTypes') {
      const _value = data.map((i: any) => i.id).toString();
      values[key] = _value;
    }
    validateFields();
  };

  const updateAutoRenewDateHandler = (val: any) => {
    if (typeof val === 'boolean') {
      values.autoRenewToggle = val;
      setAutoRenewToggle(val);
    } else {
      setAutoRenewDate(val);
    }
    validateFields();
  };

  const clearRetiredExcavationActivityTypes = useCallback(() => {
    const { activityTypes, isPlanningJob } = values;

    if (
      isPlanningJob !== false ||
      typeof activityTypes !== 'string' ||
      activityTypes.length === 0
    ) {
      return;
    }

    const hasRetiredActivityType = activityTypes
      .split(',')
      .some((activityType: string) =>
        RETIRED_ACTIVITY_TYPES.includes(activityType)
      );

    if (hasRetiredActivityType) {
      values.activityTypes = '';
      updateValues(values);
    }
  }, [values, updateValues]);

  // EFFECTS
  // useEffect(() => {
  //   setAutoRenewUntil(values.autoRenewUntil);
  //   setAutoRenewToggle(values.autoRenewToggle);
  // }, []);

  const jobStartMinDate = Utils.toLocalDateFormat(new Date());
  const jobStartMaxDate = values.digEndAt
    ? Utils.toLocalDateFormat(values.digEndAt)
    : '';
  const jobEndMinDate = digStartAtMin
    ? Utils.toLocalDateFormat(digStartAtMin)
    : Utils.toLocalDateFormat(new Date());

  const onCalendarClose = (val: any, strValue: any, self: any) => {
    strValue && self.setDate(strValue, true, 'd/m/Y');
  };

  useEffect(() => {
    if (lodgeEnquiry) {
      const { designAct, excavationAct } = lodgeEnquiry;
      const designActOptions =
        designAct?.length > 0 &&
        designAct.map((item: any) => ({
          id: item.code,
          label: item.label
        }));
      const excavationActOptions =
        excavationAct?.length > 0 &&
        excavationAct
          .filter((item: any) => !RETIRED_ACTIVITY_TYPES.includes(item.code))
          .map((item: any) => ({
            id: item.code,
            label: item.label
          }));
      setDesignActivityTypeItems(designActOptions || []);
      setExActivityTypeItems(excavationActOptions || []);
    }
  }, [lodgeEnquiry]);

  useEffect(() => {
    // Clear all activities when an Excavation enquiry uses a retired activity code.
    clearRetiredExcavationActivityTypes();
  }, [clearRetiredExcavationActivityTypes]);

  useEffect(() => {
    if (values) {
      // TODO - Set form initial values
      setDigStartAtMin(values.digStartAt);

      values.digStartAt && setStartDateInfo(values.digStartAt);

      if (values.digEndAt) {
        setShowAutoRenewToggle(
          dayjs(values.digEndAt).isAfter(NEXT_28_DAYS_DATE, 'date') ||
            values.autoRenewToggle
        );
      }

      if (values.enquiryType || values.isPlanningJob) {
        const enquiryType =
          values.isPlanningJob === true ? 'Design' : 'Excavation';
        values.enquiryType = enquiryType;
        setLodgeEnquiryType(enquiryType);
      }
    }
    validateFields();
  }, [lodgeEnquiryCache, values, NEXT_28_DAYS_DATE, validateFields]);

  useEffect(() => {
    if (isSameDayEnquiry) {
      dispatch(
        addAlert(
          sameDayWarningGroup,
          'warning',
          'Start date is today',
          <ol className="orderedList">
            <li>Asset owners need time to respond</li>
            <li>
              You are responsible (and liable) to obtain all necessary
              information before proceeding
            </li>
            <li>In the case of emergency, contact asset owner directly</li>
          </ol>,
          {
            stayForever: true
          }
        )
      );
    } else {
      dispatch(removeAlertByGroup(sameDayWarningGroup));
    }

    return () => {
      dispatch(removeAlertByGroup(sameDayWarningGroup));
    };
  }, [isSameDayEnquiry, dispatch]);

  return (
    <>
      <form
        onSubmit={handleSubmit}
        className="lodgeEnquiryFrom custom-scroller"
        noValidate
      >
        <div className="lodgeEnquiryFrom--body">
          <section className="formFieldItem datePicker--wrapper">
            <div className="startAt-body">
              <DatePicker
                minDate={jobStartMinDate}
                maxDate={jobStartMaxDate}
                dateFormat="d/m/Y"
                datePickerType="single"
                onChange={(val: any) =>
                  fieldsChangeHandler({
                    key: 'digStartAt',
                    data: val
                  })
                }
                value={
                  values.digStartAt
                    ? Utils.toLocalDateFormat(values.digStartAt)
                    : ''
                }
                onClose={onCalendarClose}
              >
                <DatePickerInput
                  id="digStartAt"
                  name="digStartAt"
                  data-testid="digStartAt"
                  labelText="Job start date"
                  placeholder="dd/mm/yyyy"
                  type="text"
                  autoComplete="off"
                />
              </DatePicker>
              {inputError('digStartAt')}
            </div>
            <div className="endAt-body">
              <DatePicker
                dateFormat="d/m/Y"
                datePickerType="single"
                minDate={jobEndMinDate}
                onChange={(val: any) =>
                  fieldsChangeHandler({
                    key: 'digEndAt',
                    data: val
                  })
                }
                value={
                  values.digEndAt
                    ? Utils.toLocalDateFormat(values.digEndAt)
                    : ''
                }
                onClose={onCalendarClose}
              >
                <DatePickerInput
                  id="digEndAt"
                  name="digEndAt"
                  labelText="Job end date"
                  data-testid="digEndAt"
                  placeholder="dd/mm/yyyy"
                  type="text"
                  autoComplete="off"
                />
              </DatePicker>
              {inputError('digEndAt')}
            </div>
          </section>
          {/* Auto Renew */}
          {showAutoRenewToggle && (
            <AutoRenewComponent
              jobEndDate={values.digEndAt}
              autoRenewUntil={autoRenewUntil}
              autoRenewToggle={autoRenewToggle}
              updateValues={(val: any) => updateAutoRenewDateHandler(val)}
              autoRenewUntilErrors={fErrors['autoRenewUntil']}
            />
          )}

          <section className="formFieldItem enquiryType">
            <span className="bx--label">Enquiry type</span>
            <br />
            <span className="bx--label txt-blue-60">
              All enquiry types are now free
            </span>
            <div className="groupFields--wrapper">
              <RadioButtonGroup
                labelPosition="right"
                name="radio-button-group"
                onChange={(val: any) => enquiryTypeChangeHandler(val)}
                valueSelected={
                  values.isPlanningJob === null
                    ? ''
                    : values.isPlanningJob === true
                    ? 'Design'
                    : 'Excavation'
                }
              >
                <RadioButton
                  id="radio-design"
                  labelText="Design"
                  value="Design"
                  onBlur={validateFields}
                />
                <RadioButton
                  id="radio-excavation"
                  labelText="Excavation"
                  value="Excavation"
                  onBlur={validateFields}
                />
              </RadioButtonGroup>
            </div>
            <div className="txt-container">
              <Information16 />
              <span className="bx--label">
                If you&apos;re only doing design and do not currently have an
                excavation activity scheduled, please don&apos;t use excavation
              </span>
            </div>
            {inputError('enquiryType')}
            {designActivityTypeItems && lodgeEnquiryType === 'Design' && (
              <MultiSelect
                id="activityTypes"
                items={designActivityTypeItems}
                direction="top"
                itemToString={(item: any) => (item ? item.label : '')}
                label="Activities"
                open={isMutliSelectExpanded}
                initialSelectedItems={defaultActivities}
                onChange={(val: any) =>
                  fieldsChangeHandler({
                    key: 'activityTypes',
                    data: val.selectedItems
                  })
                }
              />
            )}
            {exActivityTypeItems && lodgeEnquiryType === 'Excavation' && (
              <MultiSelect
                id="activityTypes"
                items={exActivityTypeItems}
                direction="top"
                itemToString={(item: any) => (item ? item.label : '')}
                label="Activities"
                open={isMutliSelectExpanded}
                initialSelectedItems={defaultActivities}
                onChange={(val: any) =>
                  fieldsChangeHandler({
                    key: 'activityTypes',
                    data: val.selectedItems
                  })
                }
              />
            )}
            {inputError('activityTypes')}
          </section>
        </div>
        <div className="progressBar--wrapper">
          <div className="progressBar completed"></div>
          <div className="progressBar completed"></div>
          <div className="progressBar"></div>
        </div>
        <section className="button--wrapper">
          <Button
            className="cancelEnquiryButton"
            kind="secondary"
            onClick={() => goToPreviousFormHandler()}
            renderIcon={Undo16}
            iconDescription="Previous"
            hasIconOnly
            tooltipPosition="bottom"
            tooltipAlignment="start"
          />
          <Button
            className="submitForm"
            type="submit"
            kind={!fErrors ? 'danger' : 'primary'}
            renderIcon={ArrowRight16}
            disabled={isButtonDisabled}
            data-testid="submit"
          >
            {'Continue'}
          </Button>
        </section>
      </form>
    </>
  );
};

JobDateSelectionForm.displayName = 'JobDateSelectionForm';

export default connector(JobDateSelectionForm);
